/**
 * The chips every view is made of (`080-calendar.md` §5 "Chip", §6, §7).
 *
 * **Colour is never the only signal** (`080` §6, `110` §1.3 — an accessibility hard rule, not
 * a preference): every chip that carries a category colour also carries the category's NAME,
 * in the chip where there is room and in the `title`/`aria-label` where there is not. And the
 * label is never rendered ON the raw category colour — the colour is a dot, a border or a
 * low-alpha tint, and the text stays on the surface background, so a user's badly-chosen neon
 * yellow cannot make an event unreadable.
 *
 * A due date is deliberately a DIFFERENT SHAPE, not a differently-coloured event: a dashed
 * edge, a `CalendarClock` icon, no colour fill. "A glance must distinguish a thing that
 * happens from a page I owe myself" (`080` §7).
 */
import { Link } from 'react-router';
import { AlertCircle, CalendarClock } from 'lucide-react';
import type { CalendarEvent, EventCategory, PageDue } from '@/api/endpoints/calendar';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { formatTime } from '@/features/calendar/datetime';
import { categoryColor, doneClass, isDone } from '@/features/calendar/eventStyle';

export function CategoryDot({ category }: { category: EventCategory | null }) {
  return (
    <span
      aria-hidden
      className="size-2 shrink-0 rounded-full border border-black/10"
      style={{ backgroundColor: categoryColor(category) }}
    />
  );
}

/** The named, text-carrying badge (`080` §6: "Agenda rows show a text `[Meeting]` badge"). */
export function CategoryBadge({ category }: { category: EventCategory | null }) {
  if (category === null) return null;

  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-sm border px-1 py-px text-2xs leading-4 whitespace-nowrap"
      style={{
        // The colour as a low-alpha tint and a border — never as the text's background.
        borderColor: category.color,
        backgroundColor: `color-mix(in srgb, ${category.color} 12%, transparent)`,
      }}
    >
      <CategoryDot category={category} />
      {category.name}
    </span>
  );
}

/**
 * The compact chip used in the month cells and the week view's all-day band:
 * `[▪ colour dot] 19:00 Meet the Guildmaster`.
 */
export function EventChip({
  event,
  tz,
  onOpen,
  className,
  showTime = true,
}: {
  event: CalendarEvent;
  tz: string;
  onOpen: (event: CalendarEvent) => void;
  className?: string;
  showTime?: boolean;
}) {
  const { t } = useTranslation();
  const label = `${event.title}${event.category === null ? '' : ` — ${event.category.name}`}${
    isDone(event) ? t('calendar.event.doneSuffix') : ''
  }`;

  return (
    <button
      type="button"
      data-testid={`event-chip-${event.id}`}
      title={label}
      aria-label={label}
      onClick={(clickEvent) => {
        clickEvent.stopPropagation(); // the cell behind us opens a NEW event; we open THIS one
        onOpen(event);
      }}
      className={cn(
        'flex h-4 w-full items-center gap-1 rounded-sm px-1 text-left text-2xs hover:bg-[var(--accent)]',
        className,
      )}
    >
      <CategoryDot category={event.category} />
      {showTime && !event.allDay && (
        <span className="shrink-0 tabular-nums text-[var(--muted-foreground)]">
          {formatTime(event.startsAt, tz)}
        </span>
      )}
      <span className={cn('truncate', doneClass(event))}>{event.title}</span>
    </button>
  );
}

/**
 * A page with a deadline. Clicking it opens the **page** — never an event dialog (`080` §7).
 * Overdue renders in the warning colour with an `AlertCircle`, so lateness is carried by an
 * icon as well as a hue.
 */
export function DueChip({
  due,
  tz,
  now,
  className,
}: {
  due: PageDue;
  tz: string;
  now: Date;
  className?: string;
}) {
  const { t } = useTranslation();
  const overdue = new Date(due.dueAt).getTime() < now.getTime();

  return (
    <Link
      to={`/p/${due.pageId}`}
      data-testid={`due-chip-${due.pageId}`}
      title={t('calendar.due.titlePrefix', { title: due.title })}
      className={cn(
        'flex h-4 w-full items-center gap-1 rounded-sm border-l-2 border-dashed px-1 text-2xs hover:bg-[var(--accent)]',
        overdue ? 'border-[var(--warning)] text-[var(--warning)]' : 'border-[var(--border)]',
        className,
      )}
    >
      {overdue ? (
        <AlertCircle className="size-3 shrink-0" strokeWidth={2} />
      ) : (
        <CalendarClock className="size-3 shrink-0" strokeWidth={2} />
      )}
      {/* The page's own icon, so it is recognisably the page (`140` §M5, criterion 9). */}
      {due.icon !== null && (
        <span aria-hidden className="shrink-0 leading-none">
          {due.icon}
        </span>
      )}
      {!due.dueAllDay && (
        <span className="shrink-0 tabular-nums opacity-70">{formatTime(due.dueAt, tz)}</span>
      )}
      <span className="truncate">{due.title}</span>
    </Link>
  );
}
